import { useState } from "react";
import {
  Alert,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "../../../../lib/styled";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import type {
  ParagraphJudgmentTrace,
  EvidenceSource,
  ResolvedVerificationTrace,
  SourceJudgmentTrace,
  WeightedSource,
} from "../../../../types/claims";
import {
  QUALITY_RATING_ENGLISH_LABEL,
  qualityRatingBucket,
} from "../../../../lib/qualityRatingBucket";
import tooltips from "./tooltips.json";
import { MonoCell, CollapsibleCell, CollapseInner } from "./trace_table_cells";

interface VerificationTracePanelProps {
  trace: ResolvedVerificationTrace;
  evidence?: EvidenceSource[];
}

function voteLabel(vote: WeightedSource["vote"]): string {
  if (vote === 1) return "SUPPORTS";
  if (vote === -1) return "CONTRADICTS";
  return "INCONCLUSIVE";
}

function voteColor(vote: WeightedSource["vote"]): "success" | "error" | "default" {
  if (vote === 1) return "success";
  if (vote === -1) return "error";
  return "default";
}

function fmt(n: number, decimals = 3): string {
  return n.toFixed(decimals);
}

const ColHeaderStack = styled(Stack)({
  cursor: "help",
  display: "inline-flex",
});

const SmallInfoIcon = styled(InfoOutlinedIcon)({
  fontSize: "0.75rem",
  opacity: 0.5,
});

const ChunkBreakdownRoot = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.action.hover,
  borderRadius: `${theme.shape.borderRadius}px`,
}));

const ParagraphBlock = styled("div")(({ theme }) => ({
  borderLeft: `3px solid ${theme.palette.divider}`,
  paddingLeft: theme.spacing(1.5),
}));

const ParagraphText = styled("div")({
  fontFamily: "monospace",
  fontSize: "0.75rem",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 160,
  overflow: "auto",
  color: "inherit",
});

const QuoteBlock = styled("div")(({ theme }) => ({
  borderLeft: `2px solid ${theme.palette.warning.main}`,
  paddingLeft: theme.spacing(1),
  paddingTop: "2px",
  paddingBottom: "2px",
}));

const ItalicBody = styled(Typography)({
  fontStyle: "italic",
  fontSize: "0.73rem",
});

const TruncatedTitleCell = styled(TableCell)({
  maxWidth: 220,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const BoldMonoCell = styled(TableCell)({
  fontFamily: "monospace",
  fontWeight: "bold",
});

const ContributionCell = styled(TableCell)<{
  $positive: boolean;
  $negative: boolean;
}>(({ theme, $positive, $negative }) => ({
  fontFamily: "monospace",
  fontWeight: "bold",
  color: $positive
    ? theme.palette.success.main
    : $negative
      ? theme.palette.error.main
      : theme.palette.text.secondary,
}));

const SignalPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const SignalCell = styled("div")({
  textAlign: "center",
  minWidth: 80,
  cursor: "help",
});

const TablePaper = styled(Paper)({
  overflow: "auto",
});

const TableHeaderSection = styled("div")(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(2)} 0`,
}));

const DenseTable = styled(Table)({
  minWidth: 900,
  "& .MuiTableCell-root": { fontSize: "0.72rem", paddingTop: "4px", paddingBottom: "4px" },
  "& .MuiTableCell-head": { fontSize: "0.68rem", fontWeight: 600 },
});

const TableDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

function ColHeader({ label, tip }: { label: string; tip: string }) {
  return (
    <Tooltip title={tip} placement="top" arrow>
      <ColHeaderStack direction="row" alignItems="center" gap={0.5}>
        {label}
        <SmallInfoIcon />
      </ColHeaderStack>
    </Tooltip>
  );
}

function ChunkBreakdown({
  judgmentTrace,
  quotes,
}: {
  judgmentTrace: SourceJudgmentTrace;
  quotes?: string[];
}) {
  return (
    <ChunkBreakdownRoot>
      <Typography variant="caption" color="text.secondary">
        {judgmentTrace.paragraphs.length} paragraph(s) retrieved
      </Typography>
      {judgmentTrace.paragraphs.map((p: ParagraphJudgmentTrace) => (
        <ParagraphBlock key={p.paragraph_index}>
          <Stack direction="row" gap={1} alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.disabled">
              {p.section_key} §{p.section_ordinal}:{p.paragraph_ordinal}
            </Typography>
          </Stack>
          <ParagraphText>{p.paragraph_text || "(text not available)"}</ParagraphText>
        </ParagraphBlock>
      ))}
      {quotes && quotes.length > 0 && (
        <>
          <Divider />
          <Typography variant="caption" color="text.secondary">
            {quotes.length} LLM excerpt(s)
          </Typography>
          {quotes.map((q, i) => (
            <QuoteBlock key={i}>
              <ItalicBody variant="body2" color="text.primary">
                "{q}"
              </ItalicBody>
            </QuoteBlock>
          ))}
        </>
      )}
    </ChunkBreakdownRoot>
  );
}

function SourceWeightRow({
  ws,
  judgmentTrace,
  quotes,
  crossDesignQuality,
}: {
  ws: WeightedSource;
  judgmentTrace: SourceJudgmentTrace | undefined;
  quotes: string[] | undefined;
  crossDesignQuality: number | undefined;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TruncatedTitleCell>
          <Typography variant="caption" title={ws.title}>
            {ws.title}
          </Typography>
        </TruncatedTitleCell>
        <TableCell>
          <Stack direction="row" gap={0.5} alignItems="center">
            <Chip
              size="small"
              label={voteLabel(ws.vote)}
              color={voteColor(ws.vote)}
              variant="outlined"
            />
            {judgmentTrace && (
              <IconButton size="small" onClick={() => setOpen((v) => !v)}>
                {open ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </Stack>
        </TableCell>
        <MonoCell align="right">
          <span title={ws.recency_note ?? undefined}>{fmt(ws.recency_weight)}</span>
        </MonoCell>
        <MonoCell align="right">{fmt(ws.design_execution_quality)}</MonoCell>
        <TableCell align="right">
          {crossDesignQuality !== undefined
            ? QUALITY_RATING_ENGLISH_LABEL[qualityRatingBucket(crossDesignQuality)]
            : "—"}
        </TableCell>
        <BoldMonoCell align="right">{fmt(ws.weight)}</BoldMonoCell>
        <ContributionCell
          align="right"
          $positive={ws.contribution > 0}
          $negative={ws.contribution < 0}
        >
          {fmt(ws.contribution)}
        </ContributionCell>
      </TableRow>
      {judgmentTrace && (
        <TableRow>
          <CollapsibleCell colSpan={8} $open={open}>
            <Collapse in={open} unmountOnExit>
              <CollapseInner>
                <ChunkBreakdown judgmentTrace={judgmentTrace} quotes={quotes} />
              </CollapseInner>
            </Collapse>
          </CollapsibleCell>
        </TableRow>
      )}
    </>
  );
}

/** Full computation trace panel for the internal verification inspector. */
export function VerificationTracePanel({ trace, evidence }: VerificationTracePanelProps) {
  const { rule_applied, signal_summary, weighted_sources, source_judgment_traces } = trace;

  const sorted = [...weighted_sources].sort(
    (a, b) => Math.abs(b.contribution) - Math.abs(a.contribution)
  );

  const judgedCount = (source_judgment_traces ?? []).filter(
    (jt) => jt.verdict !== undefined
  ).length;
  const irrelevantCount = (source_judgment_traces ?? []).filter(
    (jt) => jt.verdict === "IRRELEVANT"
  ).length;

  const judgmentBySource = new Map((source_judgment_traces ?? []).map((jt) => [jt.source_id, jt]));
  const quotesBySource = new Map((evidence ?? []).map((s) => [s.source_id, s.quotes]));
  const crossDesignQualityBySource = new Map(
    (evidence ?? []).map((s) => [s.source_id, s.cross_design_quality])
  );

  const signalValues: Record<string, string | boolean> = {
    signal: fmt(signal_summary.signal),
    total_mass: fmt(signal_summary.total_mass),
    support_mass: fmt(signal_summary.support_mass),
    contradict_mass: fmt(signal_summary.contradict_mass),
    conflict_detected: signal_summary.conflict_detected,
  };

  return (
    <Stack gap={2}>
      <Alert severity="info" icon={false}>
        <Typography variant="caption" color="text.secondary" display="block">
          Verdict rule
        </Typography>
        <Typography variant="body2" fontFamily="monospace">
          {rule_applied}
        </Typography>
      </Alert>

      {judgedCount > 0 && (
        <Alert severity={irrelevantCount === judgedCount ? "warning" : "info"} icon={false}>
          <Typography variant="body2">
            The judge read {judgedCount} source(s) and found {irrelevantCount} irrelevant to the
            claim. An irrelevant source carries no weight, so it is not in the table below; see the
            Judge column of the document selection for each source's verdict.
          </Typography>
        </Alert>
      )}

      <SignalPaper>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Signal summary
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {tooltips.signal.map(({ key, label, tip }) => (
            <Tooltip key={key} title={tip} placement="top" arrow>
              <SignalCell>
                {key === "conflict_detected" ? (
                  <Chip
                    size="small"
                    label={signal_summary.conflict_detected ? "conflict" : "no conflict"}
                    color={signal_summary.conflict_detected ? "warning" : "default"}
                  />
                ) : (
                  <Typography variant="h6" fontFamily="monospace">
                    {signalValues[key]}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  {label}
                </Typography>
              </SignalCell>
            </Tooltip>
          ))}
        </Stack>
      </SignalPaper>

      <TablePaper>
        <TableHeaderSection>
          <Typography variant="caption" color="text.secondary">
            Per-source weights ({sorted.length} sources, sorted by |contribution|) — click ▾ to
            expand paragraphs
          </Typography>
        </TableHeaderSection>
        <TableDivider />
        <DenseTable size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              {tooltips.columns.map(({ key, label, align, tip }) => (
                <TableCell key={key} align={align as "left" | "right"}>
                  <ColHeader label={label} tip={tip} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((ws) => (
              <SourceWeightRow
                key={ws.source_id}
                ws={ws}
                judgmentTrace={judgmentBySource.get(ws.source_id)}
                quotes={quotesBySource.get(ws.source_id)}
                crossDesignQuality={crossDesignQualityBySource.get(ws.source_id)}
              />
            ))}
          </TableBody>
        </DenseTable>
      </TablePaper>
    </Stack>
  );
}
